import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, Upload, FileText, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { studentsAPI, staffAPI, consultantsAPI, masterSettingsAPI } from '@/api/api';
import { useNavigate } from 'react-router-dom';

const Registration = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/transcripts/registration');
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleView = async (docId) => {
    try {
      const response = await apiClient.get(`/api/transcripts/documents/${docId}`);
      setSelectedDoc(response.data);
      setShowViewModal(true);
    } catch (error) { alert('Failed to load'); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await apiClient.delete(`/api/transcripts/documents/${docId}`); fetchDocuments(); }
    catch (error) { alert('Failed'); }
  };

  const filteredDocuments = documents.filter(doc => doc.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) || doc.roll_no?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registration Documents</h1>
          <p className="text-sm text-gray-500 mt-1">View student registration documents</p>
        </div>
        <Button onClick={() => navigate('/transcripts/upload-registration')} className="flex items-center gap-2"><Upload className="h-4 w-4" />Upload Registration</Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upload Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (<tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>) : filteredDocuments.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-500"><FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />No documents found.</td></tr>
                ) : (filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{doc.entity_name}</td>
                    <td className="px-4 py-3 text-sm">{doc.roll_no || '-'}</td>
                    <td className="px-4 py-3 text-sm">{doc.title}</td>
                    <td className="px-4 py-3 text-sm">{doc.created_at?.split('T')[0]}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full ${doc.status === 'Verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{doc.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleView(doc.id)} className="text-blue-600"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(doc.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showViewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Document Details</h2><button onClick={() => setShowViewModal(false)}><X className="h-5 w-5" /></button></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><Label className="text-gray-500">Student</Label><p className="font-medium">{selectedDoc.entity_name}</p></div>
              <div><Label className="text-gray-500">Roll No</Label><p className="font-medium">{selectedDoc.roll_no || '-'}</p></div>
            </div>
            {selectedDoc.file_data && (selectedDoc.file_type === 'pdf' ? <iframe src={selectedDoc.file_data} className="w-full h-96 border rounded" title="Doc" /> : <img src={selectedDoc.file_data} alt="Doc" className="max-w-full border rounded" />)}
            <div className="flex gap-2 pt-4">{selectedDoc.file_data && <a href={selectedDoc.file_data} download={selectedDoc.file_name} className="flex-1"><Button variant="outline" className="w-full"><Download className="h-4 w-4 mr-2" />Download</Button></a>}<Button onClick={() => setShowViewModal(false)} className="flex-1">Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;